import { baseMapConfig } from '../modules/map_basemaps';
import polygonClipping from 'polygon-clipping';

const REGIONAL_CENTER = [-23.4425, -58.4438];
const REGIONAL_ZOOM = 6;

function alertSeverityRank(alert) {
  const severity = String(alert.severity || '').toLowerCase();
  const color = String(alert.color || '').toLowerCase();

  if (/nivel rojo|extrema|extreme|grande perigo/.test(severity) || color === 'red' || ['#ff0000', '#dc2626', '#b91c1c'].includes(color)) return 3;
  if (/nivel naranja|naranja|orange|severa|severe/.test(severity) || color === 'orange' || ['#ff9800', '#f97316', '#ff9900', '#ffb300'].includes(color)) return 2;
  if (/nivel amarillo|amarillo|yellow|moderada|moderate|perigo potencial/.test(severity) || color === 'yellow' || ['#ffff00', '#facc15', '#eab308', '#fffe00'].includes(color)) return 1;
  if (severity.includes('perigo')) return 2;
  return 1;
}

function alertPane(alert) {
  return `alertsLevel${alertSeverityRank(alert)}`;
}

const paraguayDateFormatter = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'America/Asuncion'
});

function paraguayDateKey(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = paraguayDateFormatter.formatToParts(date).reduce((result, part) => {
    result[part.type] = part.value;
    return result;
  }, {});
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function activeOnDay(item, day) {
  const startsOn = item.starts_on || paraguayDateKey(item.starts_at);
  const endsOn = item.ends_on || paraguayDateKey(item.ends_at);
  return (!startsOn || startsOn <= day) && (!endsOn || endsOn >= day);
}

function regionalGroupKey(alert) {
  return [alert.source, alert.title, alert.severity, alert.color].map(value => String(value || '')).join('|');
}

function mergedGeometry(alerts) {
  const geometries = alerts.map(alert => alert.geometry && alert.geometry.coordinates).filter(Boolean);
  if (geometries.length === 0) return null;
  try {
    const coordinates = polygonClipping.union(...geometries);
    return coordinates.length > 0 ? { type: 'MultiPolygon', coordinates } : null;
  } catch (_error) {
    return null;
  }
}

function formatRegionalDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('es-PY', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    hour12: false, timeZone: 'America/Asuncion'
  }).format(date);
}

function appendPopupText(container, className, text) {
  if (!text) return;
  const element = document.createElement('p');
  element.className = className;
  element.textContent = text;
  container.appendChild(element);
}

function buildRegionalPopup(alert) {
  const popup = document.createElement('article');
  popup.className = 'regional-alert-popup';

  const source = document.createElement('div');
  source.className = `regional-alert-popup__source is-${String(alert.source).toLowerCase()}`;
  source.textContent = `${alert.source} · ${alert.country}`;
  popup.appendChild(source);

  const title = document.createElement('h3');
  title.textContent = alert.title || 'Aviso meteorológico';
  popup.appendChild(title);

  const meta = document.createElement('div');
  meta.className = 'regional-alert-popup__meta';
  const severity = document.createElement('span');
  severity.style.setProperty('--regional-alert-color', alert.color || '#64748b');
  severity.textContent = alert.severity || 'Aviso';
  meta.appendChild(severity);
  const startsAt = formatRegionalDate(alert.starts_at);
  const endsAt = formatRegionalDate(alert.ends_at);
  if (startsAt || endsAt) {
    const period = document.createElement('time');
    period.textContent = startsAt && endsAt ? `${startsAt} → ${endsAt}` : (endsAt ? `Hasta ${endsAt}` : `Desde ${startsAt}`);
    meta.appendChild(period);
  }
  popup.appendChild(meta);

  appendPopupText(popup, 'regional-alert-popup__area', alert.area);
  appendPopupText(popup, 'regional-alert-popup__description', alert.description);

  if (alert.instructions) {
    const instructions = document.createElement('div');
    instructions.className = 'regional-alert-popup__instructions';
    const label = document.createElement('strong');
    label.textContent = 'Recomendaciones: ';
    instructions.appendChild(label);
    instructions.appendChild(document.createTextNode(alert.instructions));
    popup.appendChild(instructions);
  }

  if (alert.url) {
    const link = document.createElement('a');
    link.className = 'regional-alert-popup__link skip-loading';
    link.href = alert.url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = `Consultar fuente oficial ${alert.source}`;
    popup.appendChild(link);
  }
  return popup;
}

function buildLocalPopup(sector) {
  const popup = document.createElement('article');
  popup.className = 'regional-alert-popup';

  const source = document.createElement('div');
  source.className = 'regional-alert-popup__source is-me';
  source.textContent = `Meteorología Encarnación · Aviso N.º ${sector.alert_code || ''}`.trim();
  popup.appendChild(source);

  const title = document.createElement('h3');
  title.textContent = sector.alert_title || sector.alert_type || 'Aviso anticipado';
  popup.appendChild(title);

  const meta = document.createElement('div');
  meta.className = 'regional-alert-popup__meta';
  const type = document.createElement('span');
  type.style.setProperty('--regional-alert-color', sector.color || '#64748b');
  type.textContent = sector.alert_type || 'Aviso anticipado';
  meta.appendChild(type);
  if (sector.starts_label || sector.ends_label) {
    const period = document.createElement('time');
    period.textContent = sector.starts_label && sector.ends_label
      ? `${sector.starts_label} → ${sector.ends_label}`
      : (sector.ends_label ? `Hasta ${sector.ends_label}` : `Desde ${sector.starts_label}`);
    meta.appendChild(period);
  }
  popup.appendChild(meta);

  appendPopupText(popup, 'regional-alert-popup__area', sector.states ? `Departamentos: ${sector.states}` : null);
  appendPopupText(popup, 'regional-alert-popup__description', sector.comment || sector.description);

  if (sector.url) {
    const link = document.createElement('a');
    link.className = 'regional-alert-popup__link skip-loading';
    link.href = sector.url;
    link.textContent = 'Ver aviso anticipado completo';
    popup.appendChild(link);
  }

  return popup;
}

function initializeNoticesMap() {
  const page = document.querySelector('[data-notices-page]');
  if (!page) return;
  const mapElement = document.getElementById('notices-map');
  const dataElement = document.getElementById('notices-map-data');
  if (!mapElement || !dataElement || page.dataset.noticesInitialized === 'true') return;
  if (typeof L === 'undefined') return;

  let sectors;
  try {
    sectors = JSON.parse(dataElement.textContent);
  } catch (_error) {
    sectors = [];
  }
  if (!Array.isArray(sectors)) sectors = [];

  page.dataset.noticesInitialized = 'true';
  const map = L.map(mapElement, {
    center: REGIONAL_CENTER,
    zoom: REGIONAL_ZOOM,
    scrollWheelZoom: false
  });
  page._noticesMap = map;

  const baseMap = baseMapConfig('rastertiles/voyager');
  L.tileLayer(baseMap.url, {
    attribution: baseMap.attribution,
    subdomains: baseMap.subdomains
  }).addTo(map);

  map.createPane('alertsLevel1');
  map.getPane('alertsLevel1').style.zIndex = 410;
  map.createPane('alertsLevel2');
  map.getPane('alertsLevel2').style.zIndex = 420;
  map.createPane('alertsLevel3');
  map.getPane('alertsLevel3').style.zIndex = 430;
  map.createPane('countryOutline');
  map.getPane('countryOutline').style.zIndex = 440;

  const layers = new Map();
  const dmhLayers = [];
  const regionalLayers = [];
  const regionalOutlinesByDay = new Map();
  const resetButton = page.querySelector('[data-notices-reset]');
  const selectionLabel = page.querySelector('[data-notices-selection]');
  const regionalStatus = page.querySelector('[data-notices-regional-status]');
  const regionalLegend = page.querySelector('[data-notices-regional-legend]');
  const sectorTitle = page.querySelector('[data-notices-sector-title]');
  const sectorKicker = page.querySelector('[data-notices-sector-kicker]');
  const dayEmptyState = page.querySelector('[data-notices-day-empty]');
  const descriptionsSection = page.querySelector('[data-notices-descriptions-section]');
  const mePanel = page.querySelector('[data-notices-me-panel]');
  const dmhPanel = page.querySelector('[data-notices-dmh-panel]');
  const localLegend = page.querySelector('[data-notices-local-legend]');
  const sourceButtons = Array.from(page.querySelectorAll('[data-notices-source]'));
  const dayButtons = Array.from(page.querySelectorAll('[data-notices-day]'));
  let selectedSource = 'me';
  const initialDayButton = dayButtons.find(button => button.classList.contains('is-active')) || dayButtons[0];
  let selectedDay = initialDayButton && initialDayButton.dataset ? initialDayButton.dataset.noticesDay : undefined;
  let regionalPayload = null;

  function baseStyle(sector, selected, dimmed) {
    return {
      pane: alertPane(sector),
      color: sector.color || '#dc3545', fillColor: sector.color || '#dc3545',
      fillOpacity: selected ? 0.58 : (dimmed ? 0.1 : 0.34),
      opacity: dimmed ? 0.28 : 1, weight: selected ? 4 : 2
    };
  }

  function showRegionalView() {
    map.setView(REGIONAL_CENTER, REGIONAL_ZOOM);
  }

  function selectedDayLabel() {
    const button = dayButtons.find(candidate => candidate.dataset.noticesDay === selectedDay);
    return button ? button.textContent.replace(/\s+/g, ' ').trim() : selectedDay;
  }

  function activateDay(day) {
    if (!day || day === selectedDay) return;
    selectedDay = day;
    dayButtons.forEach(button => {
      const active = button.dataset.noticesDay === selectedDay;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    map.closePopup();
  }

  function firstAvailableDay(sector) {
    const button = dayButtons.find(candidate => activeOnDay(sector, candidate.dataset.noticesDay));
    return button && button.dataset.noticesDay;
  }

  function refreshLocalLayers() {
    let visibleCount = 0;

    layers.forEach(entry => {
      const visible = selectedSource === 'me' && activeOnDay(entry.sector, selectedDay);
      if (visible) {
        visibleCount += 1;
        if (!map.hasLayer(entry.layer)) entry.layer.addTo(map);
        entry.layer.setStyle(baseStyle(entry.sector, false, false));
      } else if (map.hasLayer(entry.layer)) {
        map.removeLayer(entry.layer);
      }
    });

    return visibleCount;
  }

  function showAll() {
    if (selectedSource === 'dmh') {
      showDmhSource();
      return;
    }

    const visibleCount = refreshLocalLayers();
    if (mePanel) mePanel.hidden = false;
    if (dmhPanel) dmhPanel.hidden = true;
    if (localLegend) localLegend.hidden = false;
    page.querySelectorAll('[data-notice-sector-card]').forEach(card => {
      card.hidden = false;
      card.classList.remove('is-selected');
    });
    page.querySelectorAll('[data-notice-description]').forEach(description => {
      description.hidden = false;
    });
    if (dayEmptyState) dayEmptyState.hidden = layers.size > 0;
    if (descriptionsSection) descriptionsSection.hidden = layers.size === 0;
    if (sectorTitle) {
      sectorTitle.textContent = layers.size > 0
        ? `${layers.size} ${layers.size === 1 ? 'sector vigente' : 'sectores vigentes'} · todos los días`
        : 'Situación en Paraguay';
    }
    if (sectorKicker) sectorKicker.textContent = 'Sectores afectados';
    if (resetButton) resetButton.hidden = true;
    if (selectionLabel) {
      selectionLabel.textContent = visibleCount > 0
        ? `Mostrando avisos vigentes para ${selectedDayLabel()}. Seleccione un sector.`
        : `Sin avisos anticipados vigentes en Paraguay para ${selectedDayLabel()}.`;
    }
    showRegionalView();
  }

  function showDmhSource() {
    refreshLocalLayers();
    if (mePanel) mePanel.hidden = true;
    if (dmhPanel) dmhPanel.hidden = false;
    if (localLegend) localLegend.hidden = true;
    if (descriptionsSection) descriptionsSection.hidden = true;
    if (resetButton) resetButton.hidden = true;
    if (sectorKicker) sectorKicker.textContent = 'Fuente oficial de Paraguay';

    const visibleCount = dmhLayers.filter(entry => activeOnDay(entry.alert, selectedDay)).length;
    renderDmhPanel();
    if (sectorTitle) {
      sectorTitle.textContent = visibleCount > 0
        ? `${visibleCount} ${visibleCount === 1 ? 'aviso oficial vigente' : 'avisos oficiales vigentes'}`
        : 'Situación oficial en Paraguay';
    }
    if (selectionLabel) {
      selectionLabel.textContent = visibleCount > 0
        ? `Mostrando avisos oficiales DMH-DINAC para ${selectedDayLabel()}.`
        : `Sin avisos oficiales CAP de la DMH-DINAC para ${selectedDayLabel()}.`;
    }
    showRegionalView();
  }

  function activateSource(source) {
    selectedSource = source === 'dmh' ? 'dmh' : 'me';
    sourceButtons.forEach(button => {
      const active = button.dataset.noticesSource === selectedSource;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    map.closePopup();
    showAll();
    refreshRegionalLayers();
  }

  function selectSector(sectorId, focusMap) {
    const selectedEntry = layers.get(String(sectorId));
    if (!selectedEntry) return;

    if (!activeOnDay(selectedEntry.sector, selectedDay)) {
      const targetDay = firstAvailableDay(selectedEntry.sector);
      if (!targetDay) return;
      activateDay(targetDay);
      refreshRegionalLayers();
    }

    layers.forEach((entry, id) => {
      const selected = id === String(sectorId);
      if (selected) {
        if (!map.hasLayer(entry.layer)) entry.layer.addTo(map);
        entry.layer.setStyle(baseStyle(entry.sector, true, false));
        entry.layer.bringToFront();
      } else if (map.hasLayer(entry.layer)) {
        map.removeLayer(entry.layer);
      }
    });
    page.querySelectorAll('[data-notice-sector-card]').forEach(card => {
      const selected = card.dataset.noticeSectorCard === String(sectorId);
      card.hidden = !selected;
      card.classList.toggle('is-selected', selected);
    });
    page.querySelectorAll('[data-notice-description]').forEach(description => {
      description.hidden = description.dataset.alertId !== selectedEntry.sector.alert_id;
    });
    if (dayEmptyState) dayEmptyState.hidden = true;
    if (descriptionsSection) descriptionsSection.hidden = false;
    if (sectorTitle) sectorTitle.textContent = `Sector seleccionado · ${selectedDayLabel()}`;
    if (resetButton) resetButton.hidden = false;
    if (selectionLabel) selectionLabel.textContent = `Sector seleccionado: ${selectedEntry.sector.alert_type}.`;
    if (focusMap) map.fitBounds(selectedEntry.layer.getBounds(), { padding: [40, 40], maxZoom: 9 });
    selectedEntry.layer.openPopup();
  }

  sectors.forEach(sector => {
    const layer = L.polygon(sector.coordinates, baseStyle(sector, false, false)).addTo(map);
    layer.bindPopup(() => buildLocalPopup(sector), { maxWidth: 390, className: 'regional-alert-leaflet-popup' });
    layer.bindTooltip(sector.alert_type, { sticky: true, direction: 'top' });
    layer.on('click', () => selectSector(sector.id, false));
    layers.set(String(sector.id), { layer, sector });
  });

  fetch('/geojson/paraguay.geojson', { cache: 'force-cache' })
    .then(response => response.ok ? response.json() : null)
    .then(geojson => {
      if (!geojson || !page.isConnected) return;
      L.geoJSON(geojson, {
        interactive: false,
        style: { pane: 'countryOutline', color: '#334155', weight: 1.25, opacity: 0.78, fillOpacity: 0 }
      }).addTo(map);
    })
    .catch(() => null);

  function updateRegionalLegend(payload) {
    if (!regionalLegend) return;
    regionalLegend.hidden = false;
    ['SMN', 'INMET'].forEach(sourceName => {
      const sourceElement = regionalLegend.querySelector(`[data-regional-source="${sourceName}"]`);
      const status = payload.sources && payload.sources[sourceName.toLowerCase()];
      if (!sourceElement) return;
      sourceElement.classList.toggle('is-unavailable', status && status.ok === false);
      const count = sourceElement.querySelector('small');
      const visibleCount = regionalLayers.filter(entry => entry.alert.source === sourceName && activeOnDay(entry.alert, selectedDay)).length;
      if (count) count.textContent = status && status.ok === false ? '—' : String(visibleCount);
    });
  }

  function showRegionalStatus(message, state) {
    if (!regionalStatus) return;
    regionalStatus.hidden = false;
    regionalStatus.className = `notices-regional-status is-${state}`;
    regionalStatus.replaceChildren();
    const icon = document.createElement('i');
    icon.className = state === 'error' ? 'bi bi-cloud-slash' : 'bi bi-info-circle';
    icon.setAttribute('aria-hidden', 'true');
    regionalStatus.appendChild(icon);
    regionalStatus.appendChild(document.createTextNode(message));
  }

  function renderDmhPanel() {
    if (!dmhPanel) return;
    dmhPanel.replaceChildren();
    if (!regionalPayload) {
      const loading = document.createElement('section');
      loading.className = 'notices-empty-state notices-empty-state--compact';
      loading.textContent = 'Consultando avisos oficiales de la DMH-DINAC…';
      dmhPanel.appendChild(loading);
      return;
    }

    const status = regionalPayload.sources && regionalPayload.sources.dmh;
    const entries = dmhLayers.filter(entry => activeOnDay(entry.alert, selectedDay));
    if (entries.length === 0) {
      const empty = document.createElement('section');
      empty.className = 'notices-empty-state notices-empty-state--compact';
      const icon = document.createElement('div');
      icon.className = 'notices-empty-icon';
      icon.innerHTML = '<i class="bi bi-shield-check" aria-hidden="true"></i>';
      const title = document.createElement('h2');
      title.textContent = status && status.ok === false ? 'Fuente oficial temporalmente no disponible' : 'Sin avisos oficiales vigentes';
      const text = document.createElement('p');
      text.className = 'mb-0';
      text.textContent = status && status.ok === false
        ? 'No fue posible consultar el feed CAP de la DMH-DINAC.'
        : `La DMH-DINAC no registra avisos CAP para ${selectedDayLabel()}.`;
      empty.append(icon, title, text);
      dmhPanel.appendChild(empty);
      return;
    }

    entries.forEach((entry, index) => {
      const alert = entry.alert;
      const card = document.createElement('article');
      card.className = 'notice-sector-card is-official';
      card.style.setProperty('--notice-color', alert.color || '#64748b');

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'notice-sector-select skip-loading';
      button.setAttribute('aria-label', `Ver aviso oficial ${index + 1}: ${alert.title || 'Aviso meteorológico'}`);
      const number = document.createElement('span');
      number.className = 'notice-sector-index';
      number.textContent = String(index + 1);
      const main = document.createElement('span');
      main.className = 'notice-sector-main';
      const title = document.createElement('span');
      title.className = 'notice-sector-type';
      title.textContent = alert.title || 'Aviso meteorológico oficial';
      const label = document.createElement('span');
      label.className = 'notice-sector-alert';
      label.textContent = `DMH-DINAC · ${alert.severity || 'Aviso'}`;
      const crosshair = document.createElement('i');
      crosshair.className = 'bi bi-crosshair';
      crosshair.setAttribute('aria-hidden', 'true');
      main.append(title, label);
      button.append(number, main, crosshair);

      const details = document.createElement('div');
      details.className = 'notice-sector-details';
      const period = document.createElement('div');
      period.className = 'notice-sector-period';
      const startsAt = formatRegionalDate(alert.starts_at);
      const endsAt = formatRegionalDate(alert.ends_at);
      period.textContent = startsAt && endsAt ? `Desde ${startsAt} · Hasta ${endsAt}` : (endsAt ? `Hasta ${endsAt}` : 'Vigencia oficial');
      details.appendChild(period);
      appendPopupText(details, 'notice-sector-states mb-0', alert.area);

      button.addEventListener('click', () => {
        map.fitBounds(entry.layer.getBounds(), { padding: [34, 34], maxZoom: 8 });
        entry.layer.eachLayer(featureLayer => featureLayer.openPopup());
      });
      card.append(button, details);
      dmhPanel.appendChild(card);
    });
  }

  function addRegionalAlerts(payload) {
    const alerts = Array.isArray(payload.alerts) ? payload.alerts : [];
    alerts.sort((first, second) => alertSeverityRank(first) - alertSeverityRank(second)).forEach(alert => {
      if (!alert.geometry) return;
      const color = /^#[0-9a-f]{6}$/i.test(alert.color || '') ? alert.color : '#64748b';
      const sourceClass = String(alert.source || '').toLowerCase();
      const isDmh = alert.source === 'DMH-DINAC';
      const layer = L.geoJSON(alert.geometry, {
        pane: alertPane(alert),
        style: {
          color, fillColor: color, fillOpacity: isDmh ? 0.34 : 0.24,
          opacity: isDmh ? 0.95 : 0, weight: isDmh ? 2.5 : 0,
          className: `regional-alert-layer is-${sourceClass}`
        },
        onEachFeature: (_feature, featureLayer) => {
          featureLayer.bindPopup(() => buildRegionalPopup(alert), { maxWidth: 390, className: 'regional-alert-leaflet-popup' });
          featureLayer.bindTooltip(`${alert.source}: ${alert.title || 'Aviso meteorológico'}`, { sticky: true, direction: 'top' });
          featureLayer.on({
            mouseover: event => event.target.setStyle({ fillOpacity: isDmh ? 0.48 : 0.38 }),
            mouseout: event => event.target.setStyle({ fillOpacity: isDmh ? 0.34 : 0.24 })
          });
        }
      });
      (isDmh ? dmhLayers : regionalLayers).push({ alert, layer });
    });

    regionalPayload = payload;
    if (selectedSource === 'dmh') showDmhSource();
    refreshRegionalLayers();
  }

  function regionalOutlinesForDay(day) {
    if (regionalOutlinesByDay.has(day)) return regionalOutlinesByDay.get(day);

    const groups = new Map();
    regionalLayers.filter(entry => activeOnDay(entry.alert, day)).forEach(entry => {
      const key = regionalGroupKey(entry.alert);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(entry.alert);
    });

    const outlines = [];
    groups.forEach(alerts => {
      const geometry = mergedGeometry(alerts);
      if (!geometry) return;
      const sample = alerts[0];
      const color = /^#[0-9a-f]{6}$/i.test(sample.color || '') ? sample.color : '#64748b';
      outlines.push(L.geoJSON(geometry, {
        pane: alertPane(sample),
        interactive: false,
        style: {
          color,
          fill: false,
          opacity: 0.95,
          weight: 2.25,
          dashArray: String(sample.source).toLowerCase() === 'smn' ? '8 5' : '3 5',
          className: `regional-alert-outline is-${String(sample.source || '').toLowerCase()}`
        }
      }));
    });
    regionalOutlinesByDay.set(day, outlines);
    return outlines;
  }

  function refreshRegionalLayers() {
    if (!regionalPayload) return;
    dmhLayers.forEach(entry => {
      const visible = selectedSource === 'dmh' && activeOnDay(entry.alert, selectedDay);
      if (visible && !map.hasLayer(entry.layer)) entry.layer.addTo(map);
      if (!visible && map.hasLayer(entry.layer)) map.removeLayer(entry.layer);
    });
    regionalLayers.forEach(entry => {
      const visible = activeOnDay(entry.alert, selectedDay);
      if (visible && !map.hasLayer(entry.layer)) entry.layer.addTo(map);
      if (!visible && map.hasLayer(entry.layer)) map.removeLayer(entry.layer);
    });
    regionalOutlinesByDay.forEach(outlines => outlines.forEach(layer => {
      if (map.hasLayer(layer)) map.removeLayer(layer);
    }));
    regionalOutlinesForDay(selectedDay).forEach(layer => layer.addTo(map));

    updateRegionalLegend(regionalPayload);
    const contextSources = ['smn', 'inmet'].map(name => regionalPayload.sources && regionalPayload.sources[name]).filter(Boolean);
    const failedSources = contextSources.filter(source => source.ok === false).length;
    const visibleCount = regionalLayers.filter(entry => activeOnDay(entry.alert, selectedDay)).length;
    if (failedSources === 2) {
      showRegionalStatus('Las fuentes regionales no están disponibles temporalmente.', 'error');
    } else if (failedSources === 1) {
      showRegionalStatus('Una fuente regional no respondió; se muestra la información disponible.', 'warning');
    } else if (visibleCount === 0) {
      showRegionalStatus(`Sin alertas regionales vigentes para ${selectedDayLabel()}.`, 'empty');
    } else if (regionalStatus) {
      regionalStatus.hidden = true;
    }
  }

  if (page.dataset.regionalNoticesUrl) {
    const controller = new AbortController();
    page._regionalNoticesController = controller;
    fetch(page.dataset.regionalNoticesUrl, {
      headers: { Accept: 'application/json' }, signal: controller.signal
    })
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then(payload => { if (page.isConnected) addRegionalAlerts(payload); })
      .catch(error => {
        if (error.name !== 'AbortError') showRegionalStatus('No fue posible consultar las alertas regionales.', 'error');
      });
  }

  page.querySelectorAll('[data-notice-sector-select]').forEach(button => {
    button.addEventListener('click', () => selectSector(button.dataset.noticeSectorSelect, true));
  });
  sourceButtons.forEach(button => {
    button.addEventListener('click', () => activateSource(button.dataset.noticesSource));
  });
  dayButtons.forEach(button => {
    button.addEventListener('click', () => {
      activateDay(button.dataset.noticesDay);
      showAll();
      refreshRegionalLayers();
    });
  });
  if (resetButton) resetButton.addEventListener('click', showAll);
  showAll();
  window.setTimeout(() => map.invalidateSize(), 100);
}

document.addEventListener('turbolinks:load', initializeNoticesMap);
document.addEventListener('DOMContentLoaded', initializeNoticesMap);
document.addEventListener('turbolinks:before-cache', () => {
  const page = document.querySelector('[data-notices-page]');
  if (!page) return;
  if (page._regionalNoticesController) page._regionalNoticesController.abort();
  if (page._noticesMap) page._noticesMap.remove();
  page._noticesMap = null;
  page.dataset.noticesInitialized = 'false';
});
